'use client';

import { CoreEvent, eventNames } from '@carvana/core-events';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { useSpaNavigationTiming } from '~/context/common/GlobalLayoutContext';

function getTopLevelSegment(pathname: string) {
  return pathname.split('/')[1] ?? '';
}

// Fires a core event when the top-level path segment changes (e.g. /discover/[cvnaid] → /cars),
// which indicates a page-level SPA navigation rather than an in-page route change.
export default function SpaNavigationListener() {
  const pathname = usePathname();
  const previousPathname = useRef(pathname);
  const spaNavigationStart = useSpaNavigationTiming();

  useEffect(() => {
    const setStart = () => {
      spaNavigationStart.current = performance.now();
    };

    const handleClick = (event: MouseEvent) => {
      const link = (event.target as Element).closest('a');
      if (link?.href.startsWith(window.location.origin) && !link.target) {
        const url = new URL(link.href);
        if (
          getTopLevelSegment(url.pathname) !==
          getTopLevelSegment(window.location.pathname)
        ) {
          setStart();
        }
      }
    };

    document.addEventListener('click', handleClick, { capture: true });
    window.addEventListener('popstate', setStart);

    return () => {
      document.removeEventListener('click', handleClick, { capture: true });
      window.removeEventListener('popstate', setStart);
    };
  }, [spaNavigationStart]);

  useEffect(() => {
    const previousSegment = getTopLevelSegment(previousPathname.current ?? '');
    const currentSegment = getTopLevelSegment(pathname ?? '');

    if (previousSegment !== currentSegment) {
      CoreEvent.emit(eventNames.header.refreshOnSpaNavigation, undefined);
    }

    previousPathname.current = pathname;
  }, [pathname]);

  return null;
}
